import type { WorkHomeCount } from './features/briefing/work-home-projection';
import type { ProjectableNavigationFeature, WorkspaceNavigationSection } from './workspace-navigation';

/** A rendered menu badge: an honest, already-resolved count plus the text that names it. */
export type NavigationBadge = Readonly<{ count: number; ariaLabel: string }>;

/**
 * Composes one menu entry's badge from its own source (전자결재's pending approvals, or 점검's
 * unresolved findings) and, when the server also reports due items of the same kind, an honest
 * due clause appended to the accessible name -- never a second digit, never a client-side total.
 * Matching how a phone hides an app badge at zero, a resolved `0` renders nothing, and so do
 * `pending` (not yet known) and `unavailable` (could not be read); nothing here ever stands in a
 * fabricated number for those two.
 */
export function navigationBadgeOf(
  subject: string,
  countNoun: string,
  primary: WorkHomeCount,
  due: WorkHomeCount,
): NavigationBadge | null {
  if (primary.status !== 'ready' || !primary.count) return null;
  const dueClause = due.status === 'ready' && due.count ? ` · 마감 임박 ${due.count}건` : '';
  return {
    count: primary.count,
    ariaLabel: `${subject} ${countNoun} ${primary.count}건${dueClause}`,
  };
}

/**
 * A navigation section's own badge -- shown on the collapsed rail icon and on the flyout's group
 * heading, so a number is visible without opening the group -- is the sum of whichever of its
 * menu entries carry one. Every section the badges above ever populate holds exactly one such
 * entry today (documents-approval -> electronic-approval, field-work -> inspection-reporting),
 * but the sum holds if that ever changes.
 */
export function sectionNavigationBadge<T extends ProjectableNavigationFeature>(
  section: WorkspaceNavigationSection<T>,
  badgesByFeatureId: ReadonlyMap<string, NavigationBadge>,
): NavigationBadge | null {
  const count = section.features.reduce(
    (total, feature) => total + (badgesByFeatureId.get(feature.id)?.count ?? 0),
    0,
  );
  return count > 0 ? { count, ariaLabel: `${section.label} ${count}건` } : null;
}
